/**
 * Combo Performance Table
 * Shows Tier + PropType + Direction combinations sorted by ROI
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { getRoiColor } from '@utils/colorUtils';
import type { ReactElement } from 'react';
import type { ComboPerformance } from '../hooks/useLabDashboard';

// =============================================================================
// Types
// =============================================================================

interface ComboPerformanceTableProps {
  title: string;
  data: ComboPerformance[];
}

function getHitRateColor(hitRate: number): string {
  const percent = hitRate * 100;
  if (percent >= 55) return 'text-theme-success';
  if (percent >= 52) return 'text-theme-success';
  if (percent >= 48) return 'text-theme-text-secondary';
  if (percent >= 45) return 'text-theme-warning';
  return 'text-theme-danger';
}

// =============================================================================
// Component
// =============================================================================

export function ComboPerformanceTable({ title, data }: ComboPerformanceTableProps): ReactElement {
  if (data.length === 0) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-theme-text-secondary mb-3">{title}</h3>
        <p className="text-theme-text-tertiary text-sm">No data available</p>
      </div>
    );
  }

  // Split into profitable and unprofitable
  const profitable = data.filter((d) => d.roi > 0);
  const unprofitable = data.filter((d) => d.roi <= -10);

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      <h3 className="text-sm font-medium text-theme-text-secondary mb-4 shrink-0">{title}</h3>

      <div className="grid @5xl:grid-cols-2 gap-6">
        {/* Profitable Strategies */}
        <div className="flex flex-col max-h-80">
          <h4 className="text-xs uppercase text-theme-success font-medium mb-2 shrink-0">
            Best Strategies ({profitable.length})
          </h4>
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-theme-text-secondary text-left border-b border-theme-border">
                  <th className="pb-2 font-medium">Combo</th>
                  <th className="pb-2 font-medium w-12">W</th>
                  <th className="pb-2 font-medium w-12">L</th>
                  <th className="pb-2 font-medium w-16">Hit %</th>
                  <th className="pb-2 font-medium w-16">ROI</th>
                </tr>
              </thead>
              <tbody>
                {profitable.slice(0, 10).map((row, idx) => (
                  <tr key={idx} className="border-b border-theme-border/50 last:border-0">
                    <td className="py-2 text-gray-200">
                      <span className="text-xs">
                        {row.tier} | {formatStatLabel(row.propType)} | {row.direction}
                      </span>
                    </td>
                    <td className="py-2 text-theme-text-secondary">{row.hits}</td>
                    <td className="py-2 text-theme-text-secondary">{row.misses}</td>
                    <td className={`py-2 ${getHitRateColor(row.hitRate)}`}>
                      {(row.hitRate * 100).toFixed(1)}%
                    </td>
                    <td className={`py-2 font-medium ${getRoiColor(row.roi)}`}>
                      +{row.roi.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Unprofitable Strategies */}
        <div className="flex flex-col max-h-80">
          <h4 className="text-xs uppercase text-theme-danger font-medium mb-2 shrink-0">
            Avoid These ({unprofitable.length})
          </h4>
          <div className="overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-theme-text-secondary text-left border-b border-theme-border">
                  <th className="pb-2 font-medium">Combo</th>
                  <th className="pb-2 font-medium w-12">W</th>
                  <th className="pb-2 font-medium w-12">L</th>
                  <th className="pb-2 font-medium w-16">Hit %</th>
                  <th className="pb-2 font-medium w-16">ROI</th>
                </tr>
              </thead>
              <tbody>
                {unprofitable
                  .slice(-10)
                  .reverse()
                  .map((row, idx) => (
                    <tr key={idx} className="border-b border-theme-border/50 last:border-0">
                      <td className="py-2 text-gray-200">
                        <span className="text-xs">
                          {row.tier} | {formatStatLabel(row.propType)} | {row.direction}
                        </span>
                      </td>
                      <td className="py-2 text-theme-text-secondary">{row.hits}</td>
                      <td className="py-2 text-theme-text-secondary">{row.misses}</td>
                      <td className={`py-2 ${getHitRateColor(row.hitRate)}`}>
                        {(row.hitRate * 100).toFixed(1)}%
                      </td>
                      <td className={`py-2 font-medium ${getRoiColor(row.roi)}`}>
                        {row.roi.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
